// eslint-disable-next-line
import React, { FC, ReactNode } from 'react';
import { Theme, makeStyles } from '@material-ui/core/styles';

import Box from '@material-ui/core/Box';
import OpacityAnimation from 'components/OpacityAnimation';
import ErrorScreen from 'components/Screen/ErrorScreen';
import WarningScreen from 'components/Screen/WarningScreen';
import Loader from '../Loader';

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    margin: 'auto',
  },
}));

export type AsyncLoaderProps = {
  loading: boolean;
  loadingText?: string | ReactNode | Element;
  error?: unknown;
  errorTitle?: string;
  errorMessage?: string;
  errorDetail?: string;
  fullScreen?: boolean;
  minHeight?: number | string;
  minWidth?: number | string;
  onClickRetryButton?: () => void;
  severity?: string;
};

/**
 * @example
 * <AsyncLoader loading={true} error={false} onClickRetryButton={()=> refreshCall()}>
 *  <div>Content</div>
 * </AsyncLoader>
 */
const AsyncLoader: FC<AsyncLoaderProps> = ({
  loading,
  loadingText,
  error,
  errorTitle = '¡Lo siento! Por ahora no está disponible esta información.',
  errorMessage = 'Volvé a intentarlo más tarde',
  errorDetail,
  onClickRetryButton,
  children,
  fullScreen,
  minHeight = fullScreen ? '100vh' : 250,
  minWidth = '100%',
  severity = 'ERROR',
}) => {
  const classes = useStyles();

  if (loading) {
    return (
      <OpacityAnimation
        key="loading"
        className={classes.wrapper}
        style={{ minHeight, minWidth }}
      >
        <Box textAlign="center">
          <Loader loadingText={loadingText} />
        </Box>
      </OpacityAnimation>
    );
  }

  if (error) {
    return (
      <OpacityAnimation
        key="error"
        className={classes.wrapper}
        style={{ minHeight, minWidth, display: 'contents' }}
      >
        {severity === 'WARNING' ? (
          <WarningScreen
            errorTitle={errorTitle}
            errorMessage={errorMessage}
            errorDetail={errorDetail}
            onClickRetryButton={onClickRetryButton}
          />
        ) : (
          <ErrorScreen
            errorTitle={errorTitle}
            errorMessage={errorMessage}
            errorDetail={errorDetail}
            onClickRetryButton={onClickRetryButton}
          />
        )}
      </OpacityAnimation>
    );
  }

  return (
    <OpacityAnimation
      key="content"
      style={{ minHeight, minWidth, display: 'contents' }}
    >
      {children}
    </OpacityAnimation>
  );
};

export default AsyncLoader;
